import { EnvironmentVariables, requireEnvValue } from "../utils/config/environment-variables";
import {
  ContentfulClientApi,
  createClient,
  EntriesQueries,
  EntryQueries,
  EntrySkeletonType,
  LocaleCode,
} from "contentful";
import { Log, buildLogEntityWithoutRequest, formatMessage } from "../utils/log";
import memoryCache from "memory-cache";

let instance: ContentfulClientApi<undefined>;
let contentfulInstance: ContentfulClientApi<undefined>;

const workingCacheKey = "small-cache";

const getInstance = (): ContentfulClientApi<undefined> => {
  if (!instance) {
    const space = requireEnvValue(EnvironmentVariables.getContentfulSpace(), "CONTENTFUL_SPACE_ID");
    const accessToken = requireEnvValue(EnvironmentVariables.getContentfulAccessToken(), "CONTENTFUL_ACCESS_TOKEN");
    const environment = EnvironmentVariables.getContentfulEnvironment();
    const startTime = performance.now();
    contentfulInstance = createClient({
      space,
      accessToken,
      environment,
      responseLogger: (response: { status: number; statusText: string } | Error) => {
        const durationInMs = performance.now() - startTime;
        if (response instanceof Error) {
          Log.error(buildLogEntityWithoutRequest(formatMessage(response), response, durationInMs));
          return;
        }
        Log.info(
          buildLogEntityWithoutRequest(
            formatMessage("contentful", response.status, response.statusText),
            {
              status: response.status,
            },
            durationInMs,
          ),
        );
      },
    });

    instance = {
      ...contentfulInstance,
      getEntries: async <
        EntrySkeleton extends EntrySkeletonType = EntrySkeletonType,
        Locales extends LocaleCode = string,
      >(
        query?: EntriesQueries<EntrySkeleton, undefined>,
      ) => {
        const entriesFromCache = memoryCache.get(workingCacheKey + JSON.stringify(query));
        if (entriesFromCache) {
          return entriesFromCache;
        }
        const entries = await contentfulInstance.getEntries<EntrySkeleton, Locales>(query);
        if (entries) {
          memoryCache.put(JSON.stringify(query), entries, cacheTimeoutMilliseconds);
          memoryCache.put(workingCacheKey + JSON.stringify(query), entries, smallCacheTimeoutMilliseconds);
          return entries;
        } else {
          Log.info(buildLogEntityWithoutRequest(formatMessage("from memory", memoryCache.get(JSON.stringify(query)))));
          return memoryCache.get(JSON.stringify(query));
        }
      },
      getEntry: async <
        EntrySkeleton extends EntrySkeletonType = EntrySkeletonType,
        Locales extends LocaleCode = string,
      >(
        id: string,
        query?: EntryQueries<undefined>,
      ) => {
        const entriesFromCache = memoryCache.get(workingCacheKey + id + JSON.stringify(query));
        if (entriesFromCache) {
          return entriesFromCache;
        }
        const entries = await contentfulInstance.getEntry<EntrySkeleton, Locales>(id, query);
        if (entries) {
          memoryCache.put(id + JSON.stringify(query), entries, cacheTimeoutMilliseconds);
          memoryCache.put(workingCacheKey + id + JSON.stringify(query), entries, smallCacheTimeoutMilliseconds);
          return entries;
        } else {
          Log.info(
            buildLogEntityWithoutRequest(formatMessage("from memory" + memoryCache.get(id + JSON.stringify(query)))),
          );
          return memoryCache.get(id + JSON.stringify(query));
        }
      },
    };
  }
  return instance;
};

export const cacheTimeoutMilliseconds = 30 * 60 * 1000;
export const smallCacheTimeoutMilliseconds = 5 * 60 * 1000;

export const contentfulClient = {
  getInstance,
  cacheTimeoutMilliseconds,
};
